import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import Toast from "./Toast";
import { ImageWithFallback } from "./figma/ImageWithFallback";

interface ReportsViewProps {
  onNavigateToTests?: () => void;
}

interface Order {
  id: string;
  batchId: string;
  customerName: string;
  location: string;
  deliveryDate: Date;
  grade: string;
  quantity: number;
  poNumber: string;
  vehicleNumber: string;
}

interface TestReport {
  id: string;
  name: string;
  type: string;
  daysAfterDelivery: number;
  dueDate: Date;
  status: "completed" | "pending" | "due-today" | "overdue";
  completedDate?: string;
  testedBy?: string;
  parameters: any;
  results?: any;
  icon: string;
}

export default function ReportsView({ onNavigateToTests }: Partial<ReportsViewProps>) {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedTest, setSelectedTest] = useState<TestReport | null>(null);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testToPerform, setTestToPerform] = useState<TestReport | null>(null);
  const [showToast, setShowToast] = useState<{ message: string; type: "success" | "info" } | null>(null);

  // Form states for different test types
  const [slumpFormData, setSlumpFormData] = useState({
    slump: "",
    temperature: "",
    density: "",
  });

  const [cubeFormData, setCubeFormData] = useState({
    cube1: "",
    cube2: "",
    cube3: "",
    weight: "",
    cubeIds: "",
  });

  const today = new Date("2024-12-24");

  // Sample orders
  const orders: Order[] = [
    {
      id: "ORD-2024-001",
      batchId: "RMC-2024-12-2847",
      customerName: "Prestige Constructions Pvt Ltd",
      location: "Whitefield, Bangalore - Tower A, Floor 12-15",
      deliveryDate: new Date("2024-12-23"),
      grade: "M30",
      quantity: 8.5,
      poNumber: "PO/2024/12/847",
      vehicleNumber: "KA-51-MH-8847",
    },
    {
      id: "ORD-2024-002",
      batchId: "RMC-2024-12-2846",
      customerName: "Sobha Developers Ltd",
      location: "Electronic City, Bangalore - Block B Foundation",
      deliveryDate: new Date("2024-12-22"),
      grade: "M25",
      quantity: 12.0,
      poNumber: "PO/2024/12/846",
      vehicleNumber: "KA-03-AB-5621",
    },
    {
      id: "ORD-2024-003",
      batchId: "RMC-2024-12-2845",
      customerName: "Brigade Enterprises",
      location: "Sarjapur Road, Bangalore - Basement Slab",
      deliveryDate: new Date("2024-12-20"),
      grade: "M30",
      quantity: 15.5,
      poNumber: "PO/2024/12/845",
      vehicleNumber: "KA-05-MG-7823",
    },
  ];

  // Calculate test status
  const getTestStatus = (dueDate: Date, hasData: boolean): TestReport["status"] => {
    if (hasData) return "completed";
    if (dueDate.toDateString() === today.toDateString()) return "due-today";
    if (dueDate < today) return "overdue";
    return "pending";
  };

  // Calculate days until test
  const getDaysUntil = (dueDate: Date): number => {
    const diff = dueDate.getTime() - today.getTime();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // Generate test reports for an order
  const getTestReportsForOrder = (order: Order): TestReport[] => {
    const deliveryDate = order.deliveryDate;
    
    const slumpDate = new Date(deliveryDate);
    const day7Date = new Date(deliveryDate);
    day7Date.setDate(deliveryDate.getDate() + 7);
    const day14Date = new Date(deliveryDate);
    day14Date.setDate(deliveryDate.getDate() + 14);
    const day28Date = new Date(deliveryDate);
    day28Date.setDate(deliveryDate.getDate() + 28);

    const isSlumpCompleted = slumpDate <= today;
    const is7DayCompleted = day7Date <= today;
    const is14DayCompleted = day14Date <= today;
    const is28DayCompleted = day28Date <= today;

    return [
      {
        id: "slump-test",
        name: "Slump Test Report",
        type: "Fresh Concrete Test",
        daysAfterDelivery: 0,
        dueDate: slumpDate,
        status: getTestStatus(slumpDate, isSlumpCompleted),
        completedDate: isSlumpCompleted ? slumpDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined,
        testedBy: isSlumpCompleted ? "Rajesh Kumar" : undefined,
        icon: "🧪",
        parameters: {
          targetSlump: "75-100 mm",
          temperature: "Max 32°C",
          density: "2400-2500 kg/m³",
        },
        results: isSlumpCompleted ? {
          slump: 85,
          temperature: 28,
          density: 2450,
        } : undefined,
      },
      {
        id: "7day-strength",
        name: "7-Day Strength Test",
        type: "Cube Compression Test",
        daysAfterDelivery: 7,
        dueDate: day7Date,
        status: getTestStatus(day7Date, is7DayCompleted),
        completedDate: is7DayCompleted ? day7Date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined,
        testedBy: is7DayCompleted ? "Suresh M." : undefined,
        icon: "📊",
        parameters: {
          targetStrength: `${order.grade === "M30" ? "20.0" : "16.5"} MPa (Min)`,
          cubeSize: "150mm x 150mm x 150mm",
          samples: "3 cubes",
          curingCondition: "Water curing at 27±2°C",
        },
        results: is7DayCompleted ? {
          averageStrength: order.grade === "M30" ? 24.2 : 20.5,
          cube1: order.grade === "M30" ? 24.5 : 20.8,
          cube2: order.grade === "M30" ? 24.0 : 20.5,
          cube3: order.grade === "M30" ? 24.1 : 20.2,
          weight: 8250,
          cubeIds: "C1, C2, C3",
        } : undefined,
      },
      {
        id: "14day-strength",
        name: "14-Day Strength Test",
        type: "Cube Compression Test",
        daysAfterDelivery: 14,
        dueDate: day14Date,
        status: getTestStatus(day14Date, is14DayCompleted),
        completedDate: is14DayCompleted ? day14Date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined,
        testedBy: is14DayCompleted ? "Suresh M." : undefined,
        icon: "📈",
        parameters: {
          targetStrength: `${order.grade === "M30" ? "26.0" : "22.0"} MPa (Min)`,
          cubeSize: "150mm x 150mm x 150mm",
          samples: "3 cubes",
          curingCondition: "Water curing at 27±2°C",
        },
        results: is14DayCompleted ? {
          averageStrength: order.grade === "M30" ? 29.8 : 25.2,
          cube1: order.grade === "M30" ? 30.1 : 25.5,
          cube2: order.grade === "M30" ? 29.8 : 25.0,
          cube3: order.grade === "M30" ? 29.5 : 25.1,
          weight: 8240,
          cubeIds: "C4, C5, C6",
        } : undefined,
      },
      {
        id: "28day-strength",
        name: "28-Day Strength Test",
        type: "Cube Compression Test",
        daysAfterDelivery: 28,
        dueDate: day28Date,
        status: getTestStatus(day28Date, is28DayCompleted),
        completedDate: is28DayCompleted ? day28Date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined,
        testedBy: is28DayCompleted ? "Suresh M." : undefined,
        icon: "✓",
        parameters: {
          targetStrength: `${order.grade === "M30" ? "30.0" : "25.0"} MPa (Min)`,
          cubeSize: "150mm x 150mm x 150mm",
          samples: "3 cubes",
          curingCondition: "Water curing at 27±2°C",
        },
        results: is28DayCompleted ? {
          averageStrength: order.grade === "M30" ? 33.8 : 28.5,
          cube1: order.grade === "M30" ? 34.2 : 28.8,
          cube2: order.grade === "M30" ? 33.6 : 28.5,
          cube3: order.grade === "M30" ? 33.6 : 28.2,
          weight: 8235,
          cubeIds: "C7, C8, C9",
        } : undefined,
      },
    ];
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setShowToast({ message: `Viewing tests for ${order.batchId}`, type: "info" });
  };

  const handleBackToOrders = () => {
    setSelectedOrder(null);
    setSelectedTest(null);
    setShowPDFPreview(false);
  };

  const handleTestClick = (test: TestReport) => {
    setSelectedTest(test);
    setShowPDFPreview(true);
    setShowToast({ message: `Opening ${test.name}`, type: "info" });
  };

  const handlePerformTest = (test: TestReport) => {
    setTestToPerform(test);
    setShowTestModal(true);
    // Reset form data
    if (test.id === "slump-test") {
      setSlumpFormData({ slump: "", temperature: "", density: "" });
    } else {
      setCubeFormData({ cube1: "", cube2: "", cube3: "", weight: "", cubeIds: "" });
    }
  };

  const handleSubmitTest = () => {
    if (!testToPerform || !selectedOrder) return;

    // Validate form data
    if (testToPerform.id === "slump-test") {
      if (!slumpFormData.slump || !slumpFormData.temperature || !slumpFormData.density) {
        setShowToast({ message: "Please fill all required fields", type: "info" });
        return;
      }
    } else {
      if (!cubeFormData.cube1 || !cubeFormData.cube2 || !cubeFormData.cube3 || !cubeFormData.weight || !cubeFormData.cubeIds) {
        setShowToast({ message: "Please fill all required fields", type: "info" });
        return;
      }
    }

    // Submit test data
    setShowTestModal(false);
    setShowToast({ message: `${testToPerform.name} completed successfully!`, type: "success" });
    
    // Simulate report generation
    setTimeout(() => {
      setShowToast({ message: "Test report generated and saved", type: "success" });
    }, 1500);
  };

  const handleDownloadPDF = (test: TestReport) => {
    setShowToast({ message: `Downloading ${test.name}`, type: "success" });
  };

  const statusColors = {
    completed: { bg: "#05966908", text: "#059669", dot: "#059669" },
    "due-today": { bg: "#34D39908", text: "#34D399", dot: "#34D399" },
    pending: { bg: "#6EE7B708", text: "#6EE7B7", dot: "#6EE7B7" },
    overdue: { bg: "#A7F3D008", text: "#A7F3D0", dot: "#A7F3D0" },
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-32">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white px-4 sm:px-5 md:px-8 lg:px-12 pt-6 sm:pt-8 pb-5 sm:pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            {selectedOrder && (
              <motion.button
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBackToOrders}
                className="mr-4 p-2 rounded-xl backdrop-blur-xl"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 16L6 10L12 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </motion.button>
            )}
            <h1
              style={{
                fontSize: "clamp(28px, 6vw, 40px)",
                letterSpacing: "-0.03em",
                fontWeight: "800",
              }}
            >
              {selectedOrder ? "Test Reports" : "Quality Reports"}
            </h1>
          </div>
          <p
            className="text-white/70"
            style={{
              fontSize: "14px",
              letterSpacing: "0.01em",
              fontWeight: "500",
            }}
          >
            {selectedOrder 
              ? `${selectedOrder.batchId} • ${selectedOrder.customerName}`
              : "Order management & test documentation"}
          </p>
        </div>
      </div>

      <div className="px-4 sm:px-5 md:px-8 lg:px-12 pt-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {!selectedOrder ? (
            /* Order List View */
            <div>
              <div
                className="text-[#717182] mb-4"
                style={{ fontSize: "11px", letterSpacing: "0.05em", fontWeight: "600" }}
              >
                RECENT ORDERS
              </div>

              <div className="space-y-3">
                {orders.map((order, index) => {
                  const tests = getTestReportsForOrder(order);
                  const completedTests = tests.filter(t => t.status === "completed").length;
                  const pendingTests = tests.length - completedTests;

                  return (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleOrderClick(order)}
                      className="bg-white rounded-2xl p-4 sm:p-5 cursor-pointer"
                      style={{
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
                      }}
                    >
                      {/* Order Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="text-[#0A0F1C]"
                              style={{ fontSize: "17px", fontWeight: "700" }}
                            >
                              {order.batchId}
                            </div>
                            <div
                              className="px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: "#1B2A5B15",
                                color: "#1B2A5B",
                                fontSize: "10px",
                                fontWeight: "700",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {order.grade}
                            </div>
                          </div>
                          <div
                            className="text-[#717182] mb-2"
                            style={{ fontSize: "13px" }}
                          >
                            {order.customerName}
                          </div>
                          <div
                            className="text-[#717182]"
                            style={{ fontSize: "12px" }}
                          >
                            📍 {order.location}
                          </div>
                        </div>
                      </div>

                      {/* Order Details Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div
                          className="p-3 rounded-xl"
                          style={{ backgroundColor: "#F9FAFB" }}
                        >
                          <div
                            className="text-[#717182] mb-1"
                            style={{ fontSize: "10px", letterSpacing: "0.02em" }}
                          >
                            DELIVERY DATE
                          </div>
                          <div
                            className="text-[#0A0F1C]"
                            style={{ fontSize: "13px", fontWeight: "600" }}
                          >
                            {order.deliveryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                        <div
                          className="p-3 rounded-xl"
                          style={{ backgroundColor: "#F9FAFB" }}
                        >
                          <div
                            className="text-[#717182] mb-1"
                            style={{ fontSize: "10px", letterSpacing: "0.02em" }}
                          >
                            QUANTITY
                          </div>
                          <div
                            className="text-[#0A0F1C]"
                            style={{ fontSize: "13px", fontWeight: "600" }}
                          >
                            {order.quantity} m³
                          </div>
                        </div>
                      </div>

                      {/* Test Status Summary */}
                      <div className="flex items-center justify-between pt-3 border-t border-[#F9FAFB]">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1.5">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: "#0F3D2E" }}
                            />
                            <div
                              className="text-[#717182]"
                              style={{ fontSize: "11px" }}
                            >
                              {completedTests} Completed
                            </div>
                          </div>
                          {pendingTests > 0 && (
                            <div className="flex items-center gap-1.5">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: "#1B2A5B" }}
                              />
                              <div
                                className="text-[#717182]"
                                style={{ fontSize: "11px" }}
                              >
                                {pendingTests} Pending
                              </div>
                            </div>
                          )}
                        </div>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 12L10 8L6 4" stroke="#717182" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Test Reports List for Selected Order */
            <div>
              {/* Order Info Card */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-4 sm:p-5 mb-6"
                style={{
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
                }}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                  <div>
                    <div
                      className="text-[#717182] mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                    >
                      PO Number
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "13px", fontWeight: "600" }}
                    >
                      {selectedOrder.poNumber}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182] mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                    >
                      Vehicle
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "13px", fontWeight: "600" }}
                    >
                      {selectedOrder.vehicleNumber}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182] mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                    >
                      Grade
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "13px", fontWeight: "600" }}
                    >
                      {selectedOrder.grade}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182] mb-1"
                      style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                    >
                      Quantity
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "13px", fontWeight: "600" }}
                    >
                      {selectedOrder.quantity} m³
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Test Timeline */}
              <div
                className="text-[#717182] mb-4"
                style={{ fontSize: "11px", letterSpacing: "0.05em", fontWeight: "600" }}
              >
                TEST SCHEDULE
              </div>

              <div className="space-y-4">
                {getTestReportsForOrder(selectedOrder).map((test, index) => {
                  const daysUntil = getDaysUntil(test.dueDate);
                  const colors = statusColors[test.status];

                  return (
                    <motion.div
                      key={test.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.5 }}
                      className="bg-white rounded-2xl p-4 sm:p-5"
                      style={{
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.02)",
                      }}
                    >
                      {/* Test Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span style={{ fontSize: "20px" }}>{test.icon}</span>
                            <div
                              className="text-[#0A0F1C]"
                              style={{ fontSize: "16px", fontWeight: "700" }}
                            >
                              {test.name}
                            </div>
                          </div>
                          <div
                            className="text-[#717182] mb-2"
                            style={{ fontSize: "12px" }}
                          >
                            {test.type}
                          </div>
                          <div
                            className="px-3 py-1 rounded-full inline-block"
                            style={{
                              backgroundColor: colors.bg,
                              color: colors.text,
                              fontSize: "10px",
                              fontWeight: "700",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {test.status === "completed" && "✓ COMPLETED"}
                            {test.status === "due-today" && "⚡ DUE TODAY"}
                            {test.status === "pending" && `PENDING • DUE IN ${daysUntil} DAYS`}
                            {test.status === "overdue" && `⚠ OVERDUE BY ${Math.abs(daysUntil)} DAYS`}
                          </div>
                        </div>
                      </div>

                      {/* Test Details */}
                      <div className="space-y-3 mb-4">
                        <div
                          className="p-4 rounded-xl"
                          style={{ backgroundColor: "#F9FAFB" }}
                        >
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <div
                                className="text-[#717182] mb-1"
                                style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                              >
                                Schedule
                              </div>
                              <div
                                className="text-[#0A0F1C]"
                                style={{ fontSize: "13px", fontWeight: "600" }}
                              >
                                Day {test.daysAfterDelivery} after delivery
                              </div>
                            </div>
                            <div>
                              <div
                                className="text-[#717182] mb-1"
                                style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                              >
                                {test.status === "completed" ? "Completed On" : "Due Date"}
                              </div>
                              <div
                                className="text-[#0A0F1C]"
                                style={{ fontSize: "13px", fontWeight: "600" }}
                              >
                                {test.status === "completed" 
                                  ? test.completedDate
                                  : test.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            </div>
                          </div>

                          {test.status === "completed" && test.testedBy && (
                            <div className="pt-3 border-t border-white/50">
                              <div
                                className="text-[#717182] mb-1"
                                style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                              >
                                Tested By
                              </div>
                              <div
                                className="text-[#0A0F1C]"
                                style={{ fontSize: "13px", fontWeight: "600" }}
                              >
                                👤 {test.testedBy}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Test Parameters */}
                        <div>
                          <div
                            className="text-[#717182] mb-2"
                            style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                          >
                            Test Parameters
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {Object.entries(test.parameters).map(([key, value]) => (
                              <div
                                key={key}
                                className="p-3 rounded-lg"
                                style={{ backgroundColor: "#F9FAFB" }}
                              >
                                <div
                                  className="text-[#717182] mb-0.5"
                                  style={{ fontSize: "10px" }}
                                >
                                  {key.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                                </div>
                                <div
                                  className="text-[#0A0F1C]"
                                  style={{ fontSize: "12px", fontWeight: "600" }}
                                >
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Test Results */}
                        {test.status === "completed" && test.results && (
                          <div>
                            <div
                              className="text-[#717182] mb-2"
                              style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                            >
                              Test Results
                            </div>
                            <div
                              className="p-4 rounded-xl"
                              style={{ backgroundColor: "#0F3D2E08" }}
                            >
                              {test.id === "slump-test" ? (
                                <div className="grid grid-cols-3 gap-4">
                                  <div>
                                    <div
                                      className="text-[#717182] mb-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      SLUMP
                                    </div>
                                    <div
                                      className="text-[#0F3D2E]"
                                      style={{ fontSize: "20px", fontWeight: "700" }}
                                    >
                                      {test.results.slump} mm
                                    </div>
                                  </div>
                                  <div>
                                    <div
                                      className="text-[#717182] mb-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      TEMPERATURE
                                    </div>
                                    <div
                                      className="text-[#0F3D2E]"
                                      style={{ fontSize: "20px", fontWeight: "700" }}
                                    >
                                      {test.results.temperature}°C
                                    </div>
                                  </div>
                                  <div>
                                    <div
                                      className="text-[#717182] mb-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      DENSITY
                                    </div>
                                    <div
                                      className="text-[#0F3D2E]"
                                      style={{ fontSize: "20px", fontWeight: "700" }}
                                    >
                                      {test.results.density}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="mb-3">
                                    <div
                                      className="text-[#717182] mb-1"
                                      style={{ fontSize: "10px" }}
                                    >
                                      AVERAGE COMPRESSIVE STRENGTH
                                    </div>
                                    <div
                                      className="text-[#0F3D2E]"
                                      style={{ fontSize: "28px", fontWeight: "700" }}
                                    >
                                      {test.results.averageStrength} MPa
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#0F3D2E20]">
                                    <div>
                                      <div
                                        className="text-[#717182] mb-1"
                                        style={{ fontSize: "9px" }}
                                      >
                                        CUBE 1
                                      </div>
                                      <div
                                        className="text-[#0F3D2E]"
                                        style={{ fontSize: "14px", fontWeight: "600" }}
                                      >
                                        {test.results.cube1} MPa
                                      </div>
                                    </div>
                                    <div>
                                      <div
                                        className="text-[#717182] mb-1"
                                        style={{ fontSize: "9px" }}
                                      >
                                        CUBE 2
                                      </div>
                                      <div
                                        className="text-[#0F3D2E]"
                                        style={{ fontSize: "14px", fontWeight: "600" }}
                                      >
                                        {test.results.cube2} MPa
                                      </div>
                                    </div>
                                    <div>
                                      <div
                                        className="text-[#717182] mb-1"
                                        style={{ fontSize: "9px" }}
                                      >
                                        CUBE 3
                                      </div>
                                      <div
                                        className="text-[#0F3D2E]"
                                        style={{ fontSize: "14px", fontWeight: "600" }}
                                      >
                                        {test.results.cube3} MPa
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        {test.status === "completed" ? (
                          <>
                            <motion.button
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleTestClick(test)}
                              className="flex-1 py-3 rounded-xl"
                              style={{
                                backgroundColor: "#1B2A5B",
                                color: "white",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              👁 Preview PDF
                            </motion.button>
                            <motion.button
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleDownloadPDF(test)}
                              className="px-4 py-3 rounded-xl"
                              style={{
                                backgroundColor: "#F9FAFB",
                                color: "#1B2A5B",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 12V3M9 12L6 9M9 12L12 9M3 15H15" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </motion.button>
                          </>
                        ) : test.status === "due-today" ? (
                          <motion.button
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handlePerformTest(test)}
                            className="flex-1 py-3 rounded-xl"
                            style={{
                              backgroundColor: "#F59E0B",
                              color: "white",
                              fontSize: "13px",
                              fontWeight: "700",
                            }}
                          >
                            ⚡ Perform Test Now
                          </motion.button>
                        ) : (
                          <div
                            className="flex-1 py-3 px-4 rounded-xl text-center"
                            style={{
                              backgroundColor: "#F9FAFB",
                              color: "#717182",
                              fontSize: "13px",
                              fontWeight: "600",
                            }}
                          >
                            {test.status === "pending" 
                              ? `⏱ Available in ${daysUntil} days on ${test.dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                              : `⚠ Overdue by ${Math.abs(daysUntil)} days`}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal */}
      <AnimatePresence>
        {showPDFPreview && selectedTest && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
            style={{ backgroundColor: "rgba(10, 15, 28, 0.95)" }}
            onClick={() => setShowPDFPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-auto"
              style={{
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* PDF Header */}
              <div className="sticky top-0 bg-white border-b border-[#F9FAFB] p-4 sm:p-5 flex items-center justify-between gap-3 z-10">
                <div>
                  <div
                    className="text-[#0A0F1C]"
                    style={{ fontSize: "18px", fontWeight: "700" }}
                  >
                    {selectedTest.name}
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "13px" }}
                  >
                    {selectedOrder.batchId} • {selectedOrder.customerName}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDownloadPDF(selectedTest)}
                    className="px-4 py-2 rounded-xl flex items-center gap-2"
                    style={{
                      backgroundColor: "#1B2A5B",
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 11V3M8 11L5 8M8 11L11 8M3 13H13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Download
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowPDFPreview(false)}
                    className="p-2 rounded-xl"
                    style={{ backgroundColor: "#F9FAFB" }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 5L5 15M5 5L15 15" stroke="#0A0F1C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </motion.button>
                </div>
              </div>

              {/* PDF Content */}
              <div className="p-4 sm:p-6 md:p-8">
                {/* Company Header */}
                <div className="text-center mb-8 pb-6 border-b-2 border-[#1B2A5B]">
                  <div
                    className="text-[#1B2A5B] mb-2"
                    style={{ fontSize: "32px", fontWeight: "800", letterSpacing: "-0.02em" }}
                  >
                    PREMIUM RMC SOLUTIONS
                  </div>
                  <div
                    className="text-[#717182] mb-1"
                    style={{ fontSize: "13px" }}
                  >
                    ISO 9001:2015 Certified | NABL Accredited Laboratory
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "12px" }}
                  >
                    Central Testing Facility, Bangalore - 560100 | Lab ID: NABL-T-2024-8847
                  </div>
                </div>

                {/* Report Title */}
                <div className="text-center mb-6">
                  <div
                    className="text-[#0A0F1C] mb-2"
                    style={{ fontSize: "24px", fontWeight: "700" }}
                  >
                    {selectedTest.name.toUpperCase()}
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "13px" }}
                  >
                    Test Report ID: {selectedOrder.batchId}-{selectedTest.id.toUpperCase()}
                  </div>
                </div>

                {/* Customer Details */}
                <div
                  className="p-5 rounded-xl mb-6"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <div
                    className="text-[#1B2A5B] mb-3"
                    style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.05em" }}
                  >
                    CUSTOMER DETAILS
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Customer Name</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.customerName}</div>
                    </div>
                    <div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>PO Number</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.poNumber}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Location</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.location}</div>
                    </div>
                  </div>
                </div>

                {/* Mix Details */}
                <div
                  className="p-5 rounded-xl mb-6"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <div
                    className="text-[#1B2A5B] mb-3"
                    style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.05em" }}
                  >
                    MIX DETAILS
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Batch ID</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.batchId}</div>
                    </div>
                    <div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Grade</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.grade}</div>
                    </div>
                    <div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Quantity</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.quantity} m³</div>
                    </div>
                    <div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Vehicle</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>{selectedOrder.vehicleNumber}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Delivery Date</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>
                        {selectedOrder.deliveryDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Test Date</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>
                        {selectedTest.completedDate}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Parameters */}
                <div className="mb-6">
                  <div
                    className="text-[#1B2A5B] mb-3"
                    style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.05em" }}
                  >
                    TEST PARAMETERS
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedTest.parameters).map(([key, value]) => (
                      <div
                        key={key}
                        className="p-3 rounded-lg border"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <div className="text-[#717182]" style={{ fontSize: "11px" }}>
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div className="text-[#0A0F1C]" style={{ fontSize: "13px", fontWeight: "600" }}>
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Test Results */}
                {selectedTest.results && (
                  <div className="mb-6">
                    <div
                      className="text-[#1B2A5B] mb-3"
                      style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.05em" }}
                    >
                      TEST RESULTS
                    </div>
                    <div
                      className="p-6 rounded-xl border-2"
                      style={{ borderColor: "#0F3D2E", backgroundColor: "#0F3D2E05" }}
                    >
                      {selectedTest.id === "slump-test" ? (
                        <div className="grid grid-cols-3 gap-6">
                          <div className="text-center">
                            <div className="text-[#717182] mb-2" style={{ fontSize: "11px" }}>SLUMP VALUE</div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "32px", fontWeight: "700" }}>
                              {selectedTest.results.slump}
                            </div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "13px", fontWeight: "600" }}>mm</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[#717182] mb-2" style={{ fontSize: "11px" }}>TEMPERATURE</div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "32px", fontWeight: "700" }}>
                              {selectedTest.results.temperature}
                            </div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "13px", fontWeight: "600" }}>°C</div>
                          </div>
                          <div className="text-center">
                            <div className="text-[#717182] mb-2" style={{ fontSize: "11px" }}>DENSITY</div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "32px", fontWeight: "700" }}>
                              {selectedTest.results.density}
                            </div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "13px", fontWeight: "600" }}>kg/m³</div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="text-center mb-6 pb-6 border-b border-[#0F3D2E30]">
                            <div className="text-[#717182] mb-2" style={{ fontSize: "11px" }}>AVERAGE COMPRESSIVE STRENGTH</div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "48px", fontWeight: "800", letterSpacing: "-0.02em" }}>
                              {selectedTest.results.averageStrength}
                            </div>
                            <div className="text-[#0F3D2E]" style={{ fontSize: "16px", fontWeight: "700" }}>MPa</div>
                          </div>
                          <div>
                            <div className="text-[#717182] mb-3" style={{ fontSize: "11px" }}>INDIVIDUAL CUBE RESULTS</div>
                            <div className="grid grid-cols-3 gap-4">
                              <div className="p-3 rounded-lg text-center" style={{ backgroundColor: "white" }}>
                                <div className="text-[#717182] mb-1" style={{ fontSize: "10px" }}>Cube 1</div>
                                <div className="text-[#0F3D2E]" style={{ fontSize: "20px", fontWeight: "700" }}>
                                  {selectedTest.results.cube1}
                                </div>
                                <div className="text-[#717182]" style={{ fontSize: "10px" }}>MPa</div>
                              </div>
                              <div className="p-3 rounded-lg text-center" style={{ backgroundColor: "white" }}>
                                <div className="text-[#717182] mb-1" style={{ fontSize: "10px" }}>Cube 2</div>
                                <div className="text-[#0F3D2E]" style={{ fontSize: "20px", fontWeight: "700" }}>
                                  {selectedTest.results.cube2}
                                </div>
                                <div className="text-[#717182]" style={{ fontSize: "10px" }}>MPa</div>
                              </div>
                              <div className="p-3 rounded-lg text-center" style={{ backgroundColor: "white" }}>
                                <div className="text-[#717182] mb-1" style={{ fontSize: "10px" }}>Cube 3</div>
                                <div className="text-[#0F3D2E]" style={{ fontSize: "20px", fontWeight: "700" }}>
                                  {selectedTest.results.cube3}
                                </div>
                                <div className="text-[#717182]" style={{ fontSize: "10px" }}>MPa</div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-[#0F3D2E30]">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <div className="text-[#717182]" style={{ fontSize: "10px" }}>Cube IDs</div>
                                <div className="text-[#0A0F1C]" style={{ fontSize: "12px", fontWeight: "600" }}>
                                  {selectedTest.results.cubeIds}
                                </div>
                              </div>
                              <div>
                                <div className="text-[#717182]" style={{ fontSize: "10px" }}>Average Weight</div>
                                <div className="text-[#0A0F1C]" style={{ fontSize: "12px", fontWeight: "600" }}>
                                  {selectedTest.results.weight} gm
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Test Image */}
                <div className="mb-6">
                  <div
                    className="text-[#1B2A5B] mb-3"
                    style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.05em" }}
                  >
                    TEST DOCUMENTATION
                  </div>
                  <div className="rounded-xl overflow-hidden" style={{ height: "300px" }}>
                    <ImageWithFallback
                      src={selectedTest.id === "slump-test" 
                        ? "https://images.unsplash.com/photo-1685464196339-46a985b2049b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHNsdW1wJTIwdGVzdCUyMGNvbnN0cnVjdGlvbnxlbnwxfHx8fDE3NjY1NjAyOTd8MA&ixlib=rb-4.1.0&q=80&w=1080"
                        : "https://images.unsplash.com/photo-1559338433-316d3a858d6a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGN1YmUlMjBjb21wcmVzc2lvbiUyMHRlc3QlMjBsYWJvcmF0b3J5fGVufDF8fHx8MTc2NjU2MDI5N3ww&ixlib=rb-4.1.0&q=80&w=1080"}
                      alt={selectedTest.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Remarks & Conclusion */}
                <div
                  className="p-5 rounded-xl mb-6"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <div
                    className="text-[#1B2A5B] mb-2"
                    style={{ fontSize: "12px", fontWeight: "700", letterSpacing: "0.05em" }}
                  >
                    REMARKS
                  </div>
                  <div className="text-[#0A0F1C]" style={{ fontSize: "13px", lineHeight: "1.6" }}>
                    {selectedTest.id === "slump-test" 
                      ? "The slump value is within the specified range. The concrete is in good workable condition. Temperature and density values are acceptable as per IS 456:2000 standards."
                      : `The average compressive strength of ${selectedTest.results?.averageStrength} MPa meets the specified characteristic strength requirement of ${selectedOrder.grade}. All individual cube results are within acceptable limits as per IS 516:1959.`}
                  </div>
                </div>

                {/* Technician Signature */}
                <div className="grid grid-cols-2 gap-8 pt-6 border-t-2 border-[#E5E7EB]">
                  <div>
                    <div className="mb-3">
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Tested By</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "15px", fontWeight: "700" }}>
                        {selectedTest.testedBy}
                      </div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Senior Lab Technician</div>
                    </div>
                    <div className="border-t border-[#E5E7EB] pt-2 mt-8">
                      <div className="text-[#717182]" style={{ fontSize: "10px" }}>Signature</div>
                    </div>
                  </div>
                  <div>
                    <div className="mb-3">
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Approved By</div>
                      <div className="text-[#0A0F1C]" style={{ fontSize: "15px", fontWeight: "700" }}>
                        Dr. Arun Prakash
                      </div>
                      <div className="text-[#717182]" style={{ fontSize: "11px" }}>Chief Quality Engineer</div>
                    </div>
                    <div className="border-t border-[#E5E7EB] pt-2 mt-8">
                      <div className="text-[#717182]" style={{ fontSize: "10px" }}>Signature & Stamp</div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-[#E5E7EB] text-center">
                  <div className="text-[#717182]" style={{ fontSize: "10px" }}>
                    This is a computer-generated report. For verification, scan the QR code or visit www.premiumrmc.com/verify
                  </div>
                  <div className="text-[#717182] mt-1" style={{ fontSize: "10px" }}>
                    Report Generated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Test Performance Modal */}
      <AnimatePresence>
        {showTestModal && testToPerform && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
            style={{ backgroundColor: "rgba(10, 15, 28, 0.95)" }}
            onClick={() => setShowTestModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-auto"
              style={{
                boxShadow: "0 25px 50px rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-br from-[#1B2A5B] to-[#0A0F1C] text-white p-4 sm:p-5 md:p-6 rounded-t-2xl z-10">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span style={{ fontSize: "24px" }}>{testToPerform.icon}</span>
                      <h2
                        style={{
                          fontSize: "clamp(20px, 4vw, 24px)",
                          fontWeight: "800",
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Perform {testToPerform.name}
                      </h2>
                    </div>
                    <p
                      className="text-white/70"
                      style={{ fontSize: "13px", fontWeight: "500" }}
                    >
                      {selectedOrder.batchId} • {selectedOrder.grade} • {testToPerform.type}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowTestModal(false)}
                    className="p-2 rounded-xl backdrop-blur-xl ml-2"
                    style={{ backgroundColor: "rgba(255, 255, 255, 0.2)" }}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M15 5L5 15M5 5l10 10"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-5 md:p-6 lg:p-8">
                {/* Test Info */}
                <div
                  className="p-4 rounded-xl mb-6"
                  style={{ backgroundColor: "#F9FAFB" }}
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <div
                        className="text-[#717182] mb-1"
                        style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                      >
                        Test Date
                      </div>
                      <div
                        className="text-[#0A0F1C]"
                        style={{ fontSize: "13px", fontWeight: "600" }}
                      >
                        {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    </div>
                    <div>
                      <div
                        className="text-[#717182] mb-1"
                        style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                      >
                        Customer
                      </div>
                      <div
                        className="text-[#0A0F1C]"
                        style={{ fontSize: "13px", fontWeight: "600" }}
                      >
                        {selectedOrder.customerName}
                      </div>
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <div
                        className="text-[#717182] mb-1"
                        style={{ fontSize: "10px", letterSpacing: "0.02em", textTransform: "uppercase" }}
                      >
                        Delivery Date
                      </div>
                      <div
                        className="text-[#0A0F1C]"
                        style={{ fontSize: "13px", fontWeight: "600" }}
                      >
                        {selectedOrder.deliveryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Test Form */}
                {testToPerform.id === "slump-test" ? (
                  /* Slump Test Form */
                  <div className="space-y-5">
                    <div>
                      <div
                        className="text-[#0A0F1C] mb-4"
                        style={{ fontSize: "15px", fontWeight: "700" }}
                      >
                        Fresh Concrete Test Parameters
                      </div>
                      <div className="space-y-4">
                        {/* Slump Value */}
                        <div>
                          <label
                            className="block text-[#717182] mb-2"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            Slump Value (mm) *
                          </label>
                          <input
                            type="number"
                            value={slumpFormData.slump}
                            onChange={(e) => setSlumpFormData({ ...slumpFormData, slump: e.target.value })}
                            placeholder="Enter slump value (e.g., 85)"
                            className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                            style={{ fontSize: "14px" }}
                          />
                          <div
                            className="text-[#717182] mt-1"
                            style={{ fontSize: "11px" }}
                          >
                            Target Range: 75-100 mm
                          </div>
                        </div>

                        {/* Temperature */}
                        <div>
                          <label
                            className="block text-[#717182] mb-2"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            Temperature (°C) *
                          </label>
                          <input
                            type="number"
                            value={slumpFormData.temperature}
                            onChange={(e) => setSlumpFormData({ ...slumpFormData, temperature: e.target.value })}
                            placeholder="Enter temperature (e.g., 28)"
                            className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                            style={{ fontSize: "14px" }}
                          />
                          <div
                            className="text-[#717182] mt-1"
                            style={{ fontSize: "11px" }}
                          >
                            Maximum: 32°C
                          </div>
                        </div>

                        {/* Density */}
                        <div>
                          <label
                            className="block text-[#717182] mb-2"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            Density (kg/m³) *
                          </label>
                          <input
                            type="number"
                            value={slumpFormData.density}
                            onChange={(e) => setSlumpFormData({ ...slumpFormData, density: e.target.value })}
                            placeholder="Enter density (e.g., 2450)"
                            className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                            style={{ fontSize: "14px" }}
                          />
                          <div
                            className="text-[#717182] mt-1"
                            style={{ fontSize: "11px" }}
                          >
                            Target Range: 2400-2500 kg/m³
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Test Image */}
                    <div>
                      <div
                        className="text-[#0A0F1C] mb-3"
                        style={{ fontSize: "13px", fontWeight: "700" }}
                      >
                        Test Documentation
                      </div>
                      <div className="rounded-xl overflow-hidden" style={{ height: "200px" }}>
                        <ImageWithFallback
                          src="https://images.unsplash.com/photo-1685464196339-46a985b2049b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMHNsdW1wJTIwdGVzdCUyMGNvbnN0cnVjdGlvbnxlbnwxfHx8fDE3NjY1NjAyOTd8MA&ixlib=rb-4.1.0&q=80&w=1080"
                          alt="Slump Test"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Cube Strength Test Form */
                  <div className="space-y-5">
                    <div>
                      <div
                        className="text-[#0A0F1C] mb-4"
                        style={{ fontSize: "15px", fontWeight: "700" }}
                      >
                        Cube Compression Test Parameters
                      </div>
                      <div className="space-y-4">
                        {/* Cube Results Grid */}
                        <div>
                          <label
                            className="block text-[#717182] mb-3"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            Individual Cube Compressive Strength (MPa) *
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <input
                                type="number"
                                step="0.1"
                                value={cubeFormData.cube1}
                                onChange={(e) => setCubeFormData({ ...cubeFormData, cube1: e.target.value })}
                                placeholder="Cube 1"
                                className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                                style={{ fontSize: "14px" }}
                              />
                              <div
                                className="text-[#717182] mt-1 text-center"
                                style={{ fontSize: "10px" }}
                              >
                                Cube 1
                              </div>
                            </div>
                            <div>
                              <input
                                type="number"
                                step="0.1"
                                value={cubeFormData.cube2}
                                onChange={(e) => setCubeFormData({ ...cubeFormData, cube2: e.target.value })}
                                placeholder="Cube 2"
                                className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                                style={{ fontSize: "14px" }}
                              />
                              <div
                                className="text-[#717182] mt-1 text-center"
                                style={{ fontSize: "10px" }}
                              >
                                Cube 2
                              </div>
                            </div>
                            <div>
                              <input
                                type="number"
                                step="0.1"
                                value={cubeFormData.cube3}
                                onChange={(e) => setCubeFormData({ ...cubeFormData, cube3: e.target.value })}
                                placeholder="Cube 3"
                                className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                                style={{ fontSize: "14px" }}
                              />
                              <div
                                className="text-[#717182] mt-1 text-center"
                                style={{ fontSize: "10px" }}
                              >
                                Cube 3
                              </div>
                            </div>
                          </div>
                          {cubeFormData.cube1 && cubeFormData.cube2 && cubeFormData.cube3 && (
                            <div
                              className="mt-3 p-3 rounded-xl"
                              style={{ backgroundColor: "#0F3D2E08" }}
                            >
                              <div className="flex items-center justify-between">
                                <span
                                  className="text-[#717182]"
                                  style={{ fontSize: "11px", fontWeight: "600" }}
                                >
                                  Average Strength:
                                </span>
                                <span
                                  className="text-[#0F3D2E]"
                                  style={{ fontSize: "16px", fontWeight: "700" }}
                                >
                                  {((parseFloat(cubeFormData.cube1) + parseFloat(cubeFormData.cube2) + parseFloat(cubeFormData.cube3)) / 3).toFixed(2)} MPa
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Average Weight */}
                        <div>
                          <label
                            className="block text-[#717182] mb-2"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            Average Cube Weight (gm) *
                          </label>
                          <input
                            type="number"
                            value={cubeFormData.weight}
                            onChange={(e) => setCubeFormData({ ...cubeFormData, weight: e.target.value })}
                            placeholder="Enter average weight (e.g., 8250)"
                            className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                            style={{ fontSize: "14px" }}
                          />
                        </div>

                        {/* Cube IDs */}
                        <div>
                          <label
                            className="block text-[#717182] mb-2"
                            style={{ fontSize: "12px", fontWeight: "600" }}
                          >
                            Cube Identification Numbers *
                          </label>
                          <input
                            type="text"
                            value={cubeFormData.cubeIds}
                            onChange={(e) => setCubeFormData({ ...cubeFormData, cubeIds: e.target.value })}
                            placeholder="e.g., C1, C2, C3"
                            className="w-full px-4 py-3 rounded-xl border-2 border-[#E5E7EB] focus:border-[#1B2A5B] focus:outline-none transition-colors"
                            style={{ fontSize: "14px" }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Test Image */}
                    <div>
                      <div
                        className="text-[#0A0F1C] mb-3"
                        style={{ fontSize: "13px", fontWeight: "700" }}
                      >
                        Test Documentation
                      </div>
                      <div className="rounded-xl overflow-hidden" style={{ height: "200px" }}>
                        <ImageWithFallback
                          src="https://images.unsplash.com/photo-1559338433-316d3a858d6a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMGN1YmUlMjBjb21wcmVzc2lvbiUyMHRlc3QlMjBsYWJvcmF0b3J5fGVufDF8fHx8MTc2NjU2MDI5N3ww&ixlib=rb-4.1.0&q=80&w=1080"
                          alt="Cube Compression Test"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    {/* Target Strength Info */}
                    <div
                      className="p-4 rounded-xl"
                      style={{ backgroundColor: "#1B2A5B08" }}
                    >
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div>
                          <div
                            className="text-[#717182] mb-1"
                            style={{ fontSize: "10px" }}
                          >
                            TARGET STRENGTH ({testToPerform.daysAfterDelivery}-DAY)
                          </div>
                          <div
                            className="text-[#1B2A5B]"
                            style={{ fontSize: "16px", fontWeight: "700" }}
                          >
                            {testToPerform.parameters.targetStrength}
                          </div>
                        </div>
                        <div>
                          <div
                            className="text-[#717182] mb-1"
                            style={{ fontSize: "10px" }}
                          >
                            CUBE SIZE
                          </div>
                          <div
                            className="text-[#1B2A5B]"
                            style={{ fontSize: "16px", fontWeight: "700" }}
                          >
                            150mm³
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 mt-8 pt-6 border-t border-[#E5E7EB]">
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowTestModal(false)}
                    className="flex-1 py-3.5 rounded-xl"
                    style={{
                      backgroundColor: "#F9FAFB",
                      color: "#717182",
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmitTest}
                    className="flex-1 py-3.5 rounded-xl"
                    style={{
                      backgroundColor: "#1B2A5B",
                      color: "white",
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    ✓ Submit Test Results
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {showToast && (
          <Toast
            message={showToast.message}
            type={showToast.type}
            onClose={() => setShowToast(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
